import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { connect } from 'react-redux'
import { getUrl, uploadData, list, remove } from 'aws-amplify/storage'
import { updateUserAttributes, getCurrentUser, updatePassword, fetchAuthSession } from 'aws-amplify/auth'
import LoadingSpinner from '../../common/LoadingSpinner'
import SimpleButton from '../../common/SimpleButton'
import Dropdown from '../../common/Dropdown'
import CheckboxInput from '../../common/CheckboxInput'
import { toast } from '../../common/Toast'
import GoogleAddressInput from '../../common/GoogleAddressInput'
import TooltipedIcon from '../../common/TooltipedIcon'
import {
  EmailIcon,
  PersonIcon,
  PhoneIcon,
  WorldIcon,
  LockIcon,
  FlagIcon,
  EyeIcon,
  EyeOffIcon,
  PhotoIcon,
  CaretDownIcon,
  InformationIcon,
  BuildingIcon,
} from '../../../icons'
import languages, { getIso639_2FromLocale, getLanguageFromLocale } from '../../../utils/languages'
import { handleUpdateAuthedUser } from '../../../actions/authedUser'
import { LAST_LOGIN_DATA } from '../../../actions/root'
import countries from '../../../utils/countries.json'
import { updateUser, updateNewsLetter } from '../../../utils/requests/usersAPI'
import { formatPhone } from '../../../utils/functions'
import { convertTimeWithTAndZ } from '../../../utils/filters/date'
import { checkPermission, hasEndUserSiteAccess, profileWritePermission } from '../../../utils/permissionValidation'
import { contactUrl, privacyUrl } from '../../../utils/fixedUrls'
import { isDRRegion, drAuthRules } from '../../../utils/drConfig'
import './account-details.scss'

const AccountDetails = (props) => {
  const { t } = useTranslation()
  const isDentist = hasEndUserSiteAccess(props.userPermissions)
  const [firstName, setFirstName] = useState(props.authedUser?.firstname)
  const [lastName, setLastName] = useState(props.authedUser?.lastname)
  const [email, setEmail] = useState(props.authedUser?.email)
  const [country, setCountry] = useState(props.authedUser?.country)
  const [address, setAddress] = useState(props.authedUser?.address)
  const [state, setState] = useState(props.authedUser?.stateprovince)
  const [city, setCity] = useState(props.authedUser?.city)
  const [zip, setZip] = useState(props.authedUser?.postalcode)
  const [phone, setPhone] = useState(props.authedUser?.phone)
  const [language, setLanguage] = useState(props.authedUser?.language)
  const [groupPractice, setGroupPractice] = useState(props.authedUser?.groupPractice)
  const [userAvatar, setUserAvatar] = useState(null)
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [hasAcceptedSubscriptionTerms, setHasAcceptedSubscriptionTerms] = useState(props.authedUser?.newsletter === 'true')
  const [showCurrentPassword, setShowCurrentPassword] = useState(false)
  const [showNewPassword, setShowNewPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [showSpinner, setShowSpinner] = useState(null)
  const [isValidFirstName, setIsValidFirstName] = useState(true)
  const [isValidLastName, setIsValidLastName] = useState(true)
  const [isValidPhone, setIsValidPhone] = useState(true)
  const [hasProfileFormChanges, setHasProfileFormChanges] = useState(false)
  const [hasDetailsFormChanges, setHasDetailsFormChanges] = useState(false)
  const passwordPattern = new RegExp(/^.*(?=.{8,})(?=.*\d)((?=.*[a-z]){1})((?=.*[A-Z]){1}).*$/)
  const phonePattern = new RegExp(/^[+]?[\s./0-9]*[(]?[0-9]{1,4}[)]?[-\s./0-9]*$/g)
  const storedLoginData = localStorage.getItem(LAST_LOGIN_DATA)
  const parsedLoginData = storedLoginData ? JSON.parse(storedLoginData) : null
  const userLastLogin = parsedLoginData || props.authedUser?.lastLogin || ''

  useEffect(() => {
    let isMounted = true

    if (props.authedUser == null) {
      return
    }

    setShowSpinner(true)

    list({
      path: ({ identityId }) => `protected/${identityId}/`,
      options: { pageSize: 1000 },
    })
      .then(({ items }) => {
        if (isMounted && items.length > 0) {
          const avatarFile = items.find((item) => item.path.match(/my_avatar.*/i))

          if (avatarFile) {
            getUrl({
              path: avatarFile.path,
            }).then(({ url }) => {
              if (isMounted) {
                setUserAvatar(url.toString())
              }
            })
          }
        }
      })
      .finally(() => setShowSpinner(false))

    return () => {
      isMounted = false
    }
  }, [props.authedUser?.email])

  useEffect(() => {
    const hasChanges = firstName !== props.authedUser?.firstname || lastName !== props.authedUser?.lastname

    setHasProfileFormChanges(hasChanges)
  }, [firstName, lastName])

  useEffect(() => {
    const hasChanges =
      country !== props.authedUser?.country ||
      address !== props.authedUser?.address ||
      state !== props.authedUser?.stateprovince ||
      city !== props.authedUser?.city ||
      zip !== props.authedUser?.postalcode ||
      phone !== props.authedUser?.phone ||
      language !== props.authedUser?.language ||
      groupPractice !== props.authedUser?.groupPractice ||
      hasAcceptedSubscriptionTerms !== (props.authedUser?.newsletter === 'true')

    setHasDetailsFormChanges(hasChanges)
  }, [country, address, state, city, zip, phone, language, groupPractice, hasAcceptedSubscriptionTerms])

  const canChangePassword = () => {
    const isValidNewPassword = passwordPattern.test(newPassword)
    return currentPassword.length > 0 && isValidNewPassword && newPassword === confirmPassword
  }

  const onSelectInput = (isComponentVisible, toggleVisibility) => {
    if (toggleVisibility) {
      toggleVisibility(!isComponentVisible)
    }
  }

  const selectCountry = (country) => {
    setCountry(country)
  }

  const selectLanguage = (language) => {
    setLanguage(language)
  }

  const handleChangePassword = () => {
    setShowSpinner(true)

    updatePassword({ oldPassword: currentPassword, newPassword })
      .then(() => {
        setShowSpinner(false)
        setCurrentPassword('')
        setNewPassword('')
        setConfirmPassword('')

        toast.success(t('account_context.profile_updated'))
      })
      .catch((err) => {
        setShowSpinner(false)
        toast.error(err.message)
      })
  }

  const handleClearPasswordChanges = () => {
    setCurrentPassword('')
    setNewPassword('')
    setConfirmPassword('')
  }

  const handleCancelProfileFormChanges = () => {
    setHasProfileFormChanges(false)
    setFirstName(props.authedUser?.firstname)
    setLastName(props.authedUser?.lastname)
  }
  const handleCancelDetailsFormChanges = () => {
    setHasDetailsFormChanges(false)
    setCountry(props.authedUser?.country)
    setAddress(props.authedUser?.address)
    setState(props.authedUser?.stateprovince)
    setCity(props.authedUser?.city)
    setZip(props.authedUser?.postalcode)
    setPhone(props.authedUser?.phone)
    setLanguage(props.authedUser?.language)
    setGroupPractice(props.authedUser?.groupPractice)
    setHasAcceptedSubscriptionTerms(props.authedUser?.newsletter === 'true')
  }

  const updateDatabase = (avatarUrl, showToast, updateCategory) => {
    const { dispatch } = props

    let updatedFirstName = props.authedUser?.firstname
    let updatedLastName = props.authedUser?.lastname
    let updatedLanguage = props.authedUser?.language
    let updatedPhone = props.authedUser?.phone
    let updatedCity = props.authedUser?.city
    let updatedState = props.authedUser?.stateprovince
    let updatedCountry = props.authedUser?.country
    let updatedAddress = props.authedUser?.address
    let updatedZip = props.authedUser?.postalcode
    let updatedGroup = props.authedUser?.groupPractice
    let updatedLocale = props.authedUser?.language
    let updatedAvatarUrl = userAvatar
    let updatedNewsletter = props.authedUser?.newsletter

    switch (updateCategory) {
      case 'profile':
        updatedFirstName = firstName || ''
        updatedLastName = lastName || ''
        break
      case 'details':
        updatedLanguage = language || ''
        updatedPhone = phone || ''
        updatedCity = city || ''
        updatedState = state || ''
        updatedCountry = country || ''
        updatedAddress = address || ''
        updatedZip = zip || ''
        updatedLocale = language || ''
        updatedGroup = groupPractice || ''
        updatedNewsletter = hasAcceptedSubscriptionTerms ? 'true' : 'false'
        break
      case 'avatar':
        updatedAvatarUrl = avatarUrl
        break
    }

    dispatch(
      handleUpdateAuthedUser({
        firstname: updatedFirstName,
        lastname: updatedLastName,
        language: updatedLanguage,
        phone: updatedPhone,
        city: updatedCity,
        stateprovince: updatedState,
        country: updatedCountry,
        address: updatedAddress,
        postalcode: updatedZip,
        locale: updatedLocale,
        groupPractice: updatedGroup,
        newsletter: updatedNewsletter,
      }),
    )

    const cognitoSub = props.authedUser.cognitoSub

    updateUser(cognitoSub, {
      username: email,
      email: email,
      title: '',
      first_name: updatedFirstName,
      middle_name: '',
      last_name: updatedLastName,
      phone: updatedPhone,
      city: updatedCity,
      postal_code: updatedZip,
      state_province: updatedState,
      country: updatedCountry,
      address: updatedAddress,
      group_practice_name: updatedGroup,
      preferred_contact: '',
      identity: props.authedUser?.identity || '',
      lang: getIso639_2FromLocale(updatedLocale) || '',
      locale: updatedLanguage,
      user_group: props.authedUser?.userGroup || 'dentist',
      avatar_url: updatedAvatarUrl,
      newsletter_accepted: updatedNewsletter,
    })
      .then(() => {
        setShowSpinner(false)

        if (showToast) {
          toast.success(t('account_context.profile_updated'))
        }

        if (updateCategory === 'profile') {
          setHasProfileFormChanges(false)
        }

        if (updateCategory === 'details') {
          setHasDetailsFormChanges(false)
        }
      })
      .catch((e) => {
        setShowSpinner(false)

        console.log('Error updating backend -- ', e.message || e)

        toast.error(t('error.failure_msg'))
      })
  }

  const handleSaveProfileFormChanges = async () => {
    const isValidFirstName = firstName?.length > 0
    const isValidLastName = lastName?.length > 0

    setIsValidFirstName(isValidFirstName)
    setIsValidLastName(isValidLastName)

    if (!isValidFirstName || !isValidLastName) {
      return
    }

    setShowSpinner(true)

    await getCurrentUser()

    const result = await updateUserAttributes({
      userAttributes: {
        given_name: firstName || '',
        family_name: lastName || '',
      },
    })

    await fetchAuthSession({ forceRefresh: true })

    updateDatabase('', true, 'profile')
  }

  const handleSaveDetailsFormChanges = async () => {
    const isValidPhone = phone?.length > 0 && phonePattern.test(phone)
    setIsValidPhone(isValidPhone)

    if (!isValidPhone) {
      return
    }

    setShowSpinner(true)

    const result = await updateUserAttributes({
      userAttributes: {
        'custom:phone_number': phone ? phone.replaceAll('-', '') : '',
        locale: language || '',
        'custom:country': country || '',
        'custom:address': address || '',
        'custom:city': city || '',
        'custom:state': state || '',
        'custom:postal_code': zip || '',
        'custom:group_practice_name': groupPractice || '',
        'custom:newsletter': hasAcceptedSubscriptionTerms ? 'true' : 'false',
      },
    })

    await fetchAuthSession({ forceRefresh: true })

    if (hasAcceptedSubscriptionTerms !== (props.authedUser.newsletter === 'true')) {
      try {
        await updateNewsLetter(props.authedUser.cognitoSub, {
          newsletter: hasAcceptedSubscriptionTerms,
          note: null,
        })
      } catch (error) {
        toast.error(t([`error.${error?.response?.data?.code}`, 'error.something_wrong']))
      }
    }

    updateDatabase('', true, 'details')

    props.dispatch(handleUpdateAuthedUser({ language }))
  }

  const handleRemoveAvatar = () => {
    setShowSpinner(true)

    try {
      list({
        path: `protected/${props.authedUser?.identity}/`,
        options: { pageSize: 1000 },
      }).then(({ items }) => {
        const avatars = items.filter((item) => item.path.match(/my_avatar.*/i))

        avatars.forEach((avatar) => {
          remove({
            path: avatar.path,
          })
            .then(() => {
              toast.success('Profile picture removed successfully')
              setUserAvatar(null)
              updateDatabase(null, false, 'avatar')
            })
            .catch((err) => console.error('Error removing file:', err))
        })
      })
    } catch (error) {
      console.log('error', error)

      toast.error('An error occured while removing the profile picture')

      setShowSpinner(false)
    }
  }

  const handleUploadAvatar = async (file) => {
    let isSuccesfulUpload = true

    try {
      if (!file) {
        return
      }

      if (file?.size > 2 * 1024 * 1024) {
        toast.error('File size should be less than 2 MB')
        return
      }

      list({
        path: `protected/${props.authedUser?.identity}/`,
        options: { pageSize: 1000 },
      }).then(({ items }) => {
        const avatars = items.filter((item) => item.path.match(/my_avatar.*/i))

        avatars.forEach((avatar) => {
          remove({
            path: avatar.path,
          }).catch((err) => console.error('Remove failed:', err))
        })
      })

      const reader = new FileReader()
      reader.addEventListener('load', function () {
        setUserAvatar(reader.result)
      })
      reader.readAsDataURL(file)

      var ext = file.name.split('.').pop()
      const { path } = await uploadData({
        path: ({ identityId }) => `protected/${identityId}/my_avatar.${ext}`,
        data: file,
        options: {
          contentType: 'image/*',
        },
      }).result
      toast.success('Profile picture updated successfully')
      const { url } = await getUrl({
        path: path,
      })
    } catch (error) {
      console.log('error', error)

      toast.error('An error occured while updating profile picture')

      isSuccesfulUpload = false
    }

    if (isSuccesfulUpload) {
      const avatarUrl = `protected/${props.authedUser.identity}/my_avatar.${ext}`
      updateDatabase(avatarUrl, false, 'avatar')
    }
  }

  const handleFileInput = async (event) => {
    if (!checkPermission(props.userPermissions, profileWritePermission)) {
      return
    }

    const file = event?.target?.files[0]

    await handleUploadAvatar(file)
  }

  const formatLastLogin = (lastLogin) => {
    const locationParts = [lastLogin.lastLoginLocation?.city, lastLogin.lastLoginLocation?.stateProvince?.code, lastLogin.lastLoginLocation?.country].filter(
      Boolean,
    )

    const formattedDate = convertTimeWithTAndZ(lastLogin.lastLoginDate, true)
    const location = locationParts.length ? locationParts.join(', ') : ''

    return { date: formattedDate, location }
  }

  const hasValidLastLoginValues = (lastLogin) =>
    Object.values(lastLogin).some((value) => (typeof value === 'object' ? hasValidLastLoginValues(value) : Boolean(value)))

  const renderTooltipText = () => {
    return (
      <div className="tooltip-text">
        <strong className="tooltip-header">{t('account_context.this_wasnt_you')}</strong>
        <p className="tooltip-body">{t('account_context.this_wasnt_you_body')}</p>
      </div>
    )
  }

  return (
    <div className="account-details-wrapper d-flex flex-column">
      {isDRRegion && (
        <div className="dr-warning-banner">
          <div className="dr-warning-content">
            <span className="dr-warning-text">{t('dr.profile_updates_disabled')}</span>
            <button
              className="dr-warning-close"
              onClick={(e) => {
                e.target.closest('.dr-warning-banner').style.display = 'none'
              }}
              aria-label={t('close')}
            >
              &times;
            </button>
          </div>
        </div>
      )}
      <div className="sections-wrapper">
        <p className="wrapper-title">{t('account_context.profile_details')}</p>
        <div className="section">
          <div className="section-title">
            <p>{t('account_context.profile_picture')}</p>
            <p className="disclaimer">{t('account_context.photo_discalimer')}</p>
          </div>
          <div className="section-content">
            <div className="avatar-wrapper">
              <label className="file-input-wrapper" htmlFor="file-input">
                <div className="avatar-img">
                  {userAvatar ? (
                    <img className="image" src={userAvatar} alt="avatar" />
                  ) : (
                    <div className="avatar-initials">
                      <span>{`${firstName?.charAt(0)}${lastName?.charAt(0)}`}</span>
                    </div>
                  )}
                </div>
                <PhotoIcon />
              </label>
              <input className="file-input" id="file-input" onChange={(e) => handleFileInput(e)} type="file" accept="image/*" />
            </div>
            <SimpleButton
              className="action-button remove-image-button"
              onClick={() => handleRemoveAvatar()}
              disabled={!userAvatar || showSpinner}
              requiredPermission={profileWritePermission}
            >
              {t('remove')}
            </SimpleButton>
          </div>
        </div>
        <div className="section">
          <div className="section-title">
            <p>{t('users-management.last-login')}</p>
          </div>
          {hasValidLastLoginValues(userLastLogin) && (
            <div className="section-content info-icon">
              <span>
                {formatLastLogin(userLastLogin).date}
                {!formatLastLogin(userLastLogin).location && <TooltipedIcon icon={<InformationIcon />} tooltipText={renderTooltipText()} />}
                {formatLastLogin(userLastLogin).location && (
                  <>
                    <br />
                    {formatLastLogin(userLastLogin).location}
                    <TooltipedIcon icon={<InformationIcon />} tooltipText={renderTooltipText()} />
                  </>
                )}
              </span>
            </div>
          )}
        </div>
        <div className="separator" />
        <div className="section">
          <div className="section-title">{t('account_context.account_type')}</div>
          <div className="section-content">
            <div className="input-item-wrapper">
              <div className="input-item-icon">
                <PersonIcon />
              </div>
              <input
                readOnly
                disabled
                type="text"
                className="input-item"
                value={t([`register.${props.authedUser?.userGroup?.toLowerCase()?.split('-')[0]}`, props.authedUser?.userGroup?.toLowerCase()?.split('-')[0]])}
              />
            </div>
          </div>
        </div>
        <div className="section">
          <div className="section-title">{t('full_name')}</div>
          <div className="section-content">
            <div className={'input-item-wrapper' + (isValidFirstName ? '' : ' has-error')}>
              <div className="input-item-icon">
                <PersonIcon />
              </div>
              <input type="text" className="input-item" value={firstName} onChange={(e) => setFirstName(e.target.value)} placeholder={t('first_name')} />
              {!isValidFirstName && <p className="error-message">{t('error.firstname')}</p>}
            </div>
            <div className={'input-item-wrapper' + (isValidLastName ? '' : ' has-error')}>
              <div className="input-item-icon">
                <PersonIcon />
              </div>
              <input
                type="text"
                name="lastname"
                className="input-item"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                placeholder={t('last_name')}
              />
              {!isValidLastName && <p className="error-message">{t('error.lastname')}</p>}
            </div>
          </div>
        </div>
        <div className="section">
          <div className="section-title">{t('email_address')}</div>
          <div className="section-content">
            <div className="input-item-wrapper larger-input">
              <div className="input-item-icon">
                <EmailIcon />
              </div>
              <input readOnly disabled type="text" className="input-item" name="email" value={email} />
            </div>
          </div>
        </div>
        <div className="section full-width">
          <div className="section-title empty" />
          <div className="section-content buttons">
            <SimpleButton className="cancel-button" disabled={!hasProfileFormChanges} onClick={() => handleCancelProfileFormChanges()}>
              {t('cancel')}
            </SimpleButton>
            <SimpleButton
              className="submit-button"
              disabled={!hasProfileFormChanges || showSpinner || !drAuthRules.isProfileUpdateAllowed}
              onClick={() => handleSaveProfileFormChanges()}
              requiredPermission={profileWritePermission}
            >
              {t('save')}
            </SimpleButton>
          </div>
        </div>
      </div>

      <div className="sections-wrapper">
        <p className="wrapper-title">{t('account_context.manage_password')}</p>
        <div className="section">
          <div className="section-title">{t('account_context.current_password')}</div>
          <div className="section-content">
            <div className="input-item-wrapper">
              <div className="input-item-icon">
                <LockIcon />
              </div>
              <input
                type={showCurrentPassword ? 'text' : 'password'}
                className="input-item"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
              />
              <div className="input-item-icon is-action" onClick={() => setShowCurrentPassword((prevValue) => !prevValue)}>
                {showCurrentPassword ? <EyeOffIcon /> : <EyeIcon />}
              </div>
            </div>
          </div>
        </div>
        <div className="section">
          <div className="section-title">{t('account_context.new_password')}</div>
          <div className="section-content">
            <div className="input-item-wrapper">
              <div className="input-item-icon">
                <LockIcon />
              </div>
              <input type={showNewPassword ? 'text' : 'password'} className="input-item" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} />
              <div className="input-item-icon is-action" onClick={() => setShowNewPassword((prevValue) => !prevValue)}>
                {showNewPassword ? <EyeOffIcon /> : <EyeIcon />}
              </div>
            </div>
          </div>
        </div>
        <div className="section">
          <div className="section-title">{t('account_context.confirm_new_password')}</div>
          <div className="section-content">
            <div className="input-item-wrapper">
              <div className="input-item-icon">
                <LockIcon />
              </div>
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                className="input-item"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
              />
              <div className="input-item-icon is-action" onClick={() => setShowConfirmPassword((prevValue) => !prevValue)}>
                {showConfirmPassword ? <EyeOffIcon /> : <EyeIcon />}
              </div>
            </div>
          </div>
        </div>
        <div className="section">
          <div className="section-title empty" />
          <div className="section-content">
            <p className="disclaimer">{t('account_context.password_message')}</p>
          </div>
        </div>
        <div className="section full-width">
          <div className="section-title empty" />
          <div className="section-content buttons">
            <SimpleButton className="cancel-button" disabled={!canChangePassword()} onClick={() => handleClearPasswordChanges()}>
              {t('cancel')}
            </SimpleButton>
            <SimpleButton
              className="submit-button"
              disabled={!canChangePassword() || showSpinner || !drAuthRules.isProfileUpdateAllowed}
              onClick={() => handleChangePassword()}
              requiredPermission={profileWritePermission}
            >
              {t('save')}
            </SimpleButton>
          </div>
        </div>
      </div>

      <div className="sections-wrapper">
        <p className="wrapper-title">{t('account_context.other_account_details')}</p>
        <div className="section">
          <div className="section-title">{t('account_context.contact')}</div>
          <div className="section-content">
            <div className={'input-item-wrapper' + (isValidPhone ? '' : ' has-error')}>
              <div className="input-item-icon">
                <PhoneIcon />
              </div>
              <input
                type="text"
                className="input-item"
                value={formatPhone(phone)}
                onChange={(e) => setPhone(formatPhone(e.target.value))}
                placeholder={t('phone')}
              />
              {!isValidPhone && <p className="error-message">{t('error.phone')}</p>}
            </div>
          </div>
        </div>
        <div className="section">
          <div className="section-title empty" />
          <div className="section-content">
            <Dropdown
              className="dropdown-input"
              placeholder={t('country')}
              options={countries.map((country) => ({ key: country['code'], value: country['name'] }))}
              initialSelectedKey={country}
              onChange={(country) => selectCountry(country.key)}
              withFiltering
            />
          </div>
        </div>
        <div className="section">
          <div className="section-title empty" />
          <div className="section-content">
            <GoogleAddressInput
              country={country}
              address={address || ''}
              handleSetAddress={(e) => setAddress(e)}
              city={city}
              handleSetCity={(e) => setCity(e)}
              stateProvince={state}
              handleSetStateProvince={(e) => setState(e)}
              zipCode={zip}
              handleSetZipCode={(e) => setZip(e)}
              withoutLabels
              withIcons
            />
          </div>
        </div>
        {isDentist && (
          <div className="section">
            <div className="section-title empty" />
            <div className="section-content">
              <div className={'input-item-wrapper' + (isValidLastName ? '' : ' has-error')}>
                <div className="input-item-icon">
                  <BuildingIcon />
                </div>
                <input
                  type="text"
                  className="input-item"
                  value={groupPractice}
                  onChange={(e) => setGroupPractice(e.target.value)}
                  placeholder={t('group_practice_name')}
                />
                <div className="input-helper-text">{t('if_applicable')}</div>
              </div>
            </div>
          </div>
        )}
        <div className="separator" />
        <div className="section">
          <div className="section-title">{t('language')}</div>
          <div className="section-content">
            <Dropdown
              className="dropdown-input"
              placeholder={t('language')}
              options={Object.keys(languages).map((languageOption) => ({
                key: languageOption,
                value: t([`${getLanguageFromLocale(languageOption.replace('_', '-'))}`, `${getLanguageFromLocale('en-CA')}`]),
              }))}
              initialSelectedKey={language}
              onChange={(language) => selectLanguage(language.key)}
            />
          </div>
        </div>
        <div className="separator" />
        <div className="section">
          <div className="section-title">{t('newsletter')}</div>
          <div className="section-content">
            <div className="subscription-wrapper">
              <CheckboxInput checked={hasAcceptedSubscriptionTerms} onChange={() => setHasAcceptedSubscriptionTerms(!hasAcceptedSubscriptionTerms)} />
              <p>
                {t('account_context.newsletter-description')}
                <a className="link" href={privacyUrl} target="_blank" rel="noreferrer">
                  {t('privacy_policy')}
                </a>
                {','}
                <a className="link" href={contactUrl} target="_blank" rel="noreferrer">
                  {t('contact_us')}
                </a>
                {'.'}
              </p>
            </div>
          </div>
        </div>
        <div className="section full-width">
          <div className="section-title empty" />
          <div className="section-content buttons">
            <SimpleButton className="cancel-button" disabled={!hasDetailsFormChanges} onClick={() => handleCancelDetailsFormChanges()}>
              {t('cancel')}
            </SimpleButton>
            <SimpleButton
              className="submit-button"
              disabled={!hasDetailsFormChanges || showSpinner || !drAuthRules.isProfileUpdateAllowed}
              onClick={() => handleSaveDetailsFormChanges()}
              requiredPermission={profileWritePermission}
            >
              {t('save')}
            </SimpleButton>
          </div>
        </div>
      </div>
      {showSpinner && (
        <div className={'spinner-wrapper'}>
          <LoadingSpinner />
        </div>
      )}
    </div>
  )
}

function stateToProps({ authedUser, userPermissions }) {
  return {
    authedUser,
    userPermissions,
  }
}

export default connect(stateToProps)(AccountDetails)

